/**
 * 統一 Fetcher 函數
 * 用於 SWR 的資料獲取，統一處理錯誤與認證
 */

import { requestLock } from './request-lock.js';

/**
 * API 錯誤類別
 */
export class ApiError extends Error {
  status: number;
  message: string;
  details?: unknown;
  code?: string; // 錯誤代碼（來自後端）

  constructor(
    status: number,
    message: string,
    details?: unknown,
    code?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.message = message;
    this.details = details;
    this.code = code;
  }
}

/**
 * 統一 Fetcher 函數
 *
 * @param url - API 端點 URL
 * @param options - Fetch 選項（包含 headers、method 等）
 * @returns API 回應資料
 * @throws {ApiError} 當 API 回應非 2xx 狀態碼時
 *
 * 注意事項：
 * - 非 2xx 狀態碼必須 throw Error，以便 SWR 的 error 物件能捕捉到
 * - 錯誤必須包含後端回傳的錯誤訊息 (message) 與狀態碼 (status)
 */
export async function fetcher<T = unknown>(
  url: string,
  options?: RequestInit
): Promise<T> {
  // 等待請求鎖釋放（如果已鎖定）
  if (requestLock.locked) {
    console.log('[Fetcher] 請求被鎖定，等待釋放:', requestLock.reason);
    try {
      await requestLock.waitForUnlock();
    } catch (error) {
      // 等待超時或隊列已滿，直接拋出錯誤
      throw new ApiError(
        503,
        error instanceof Error ? error.message : '請求等待超時',
        undefined,
        'REQUEST_LOCK_TIMEOUT'
      );
    }
  }

  // 處理相對路徑：如果是相對路徑（以 / 開頭），加上 API base URL
  let finalUrl = url;
  if (url.startsWith('/') && !url.startsWith('//')) {
    const apiBaseUrl =
      import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    finalUrl = `${apiBaseUrl}${url}`;
    if (import.meta.env.DEV || url.includes('feature-flags')) {
      console.log('[Fetcher] 轉換相對路徑為絕對路徑:', {
        originalUrl: url,
        finalUrl,
      });
    }
  }

  // 除錯：記錄請求資訊
  if (import.meta.env.DEV || url.includes('feature-flags')) {
    console.log('[Fetcher] 發送請求:', {
      originalUrl: url,
      finalUrl,
      method: options?.method || 'GET',
      hasHeaders: !!options?.headers,
      headers: options?.headers ? Object.keys(options.headers) : [],
    });
  }

  const response = await fetch(finalUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  // 處理非 2xx 狀態碼
  if (!response.ok) {
    let errorData: { message?: string; code?: string; details?: unknown } = {};

    try {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        const text = await response.text();
        errorData = { message: text || 'Unknown error' };
      }
    } catch {
      // 無法解析錯誤回應時，使用預設訊息
      errorData = {
        message: `HTTP ${response.status}: ${response.statusText}`,
      };
    }

    // 建立包含狀態碼與錯誤訊息的 Error
    const error = new ApiError(
      response.status,
      errorData.message || `API Error: ${response.status}`,
      errorData.details,
      errorData.code
    );

    // 處理 401 Unauthorized 錯誤（在拋出錯誤之前）
    // 注意：使用 setTimeout 延遲清除，避免影響正在進行的其他請求
    if (response.status === 401) {
      // 檢查是否為認證相關的錯誤（避免在登入頁面也清除）
      const currentPath =
        typeof window !== 'undefined' ? window.location.pathname : '';
      const isAuthPage =
        currentPath === '/' ||
        currentPath.startsWith('/tenant-register') ||
        currentPath.startsWith('/tenant-select') ||
        currentPath.startsWith('/oauth-callback');

      if (!isAuthPage && typeof window !== 'undefined') {
        // 使用 setTimeout 延遲清除，避免影響正在進行的其他請求
        // 這樣可以確保當前請求完成後再清除認證資訊
        setTimeout(() => {
          // 再次檢查路徑（避免在清除期間用戶已導航到登入頁面）
          const currentPathAfterDelay = window.location.pathname;
          const isAuthPageAfterDelay =
            currentPathAfterDelay === '/' ||
            currentPathAfterDelay.startsWith('/tenant-register') ||
            currentPathAfterDelay.startsWith('/tenant-select') ||
            currentPathAfterDelay.startsWith('/oauth-callback');

          if (!isAuthPageAfterDelay) {
            // 清除認證資訊
            localStorage.removeItem('tenant_token');
            localStorage.removeItem('tenant_id');
            localStorage.removeItem('staff_session_token');
            localStorage.removeItem('staff_id');

            // 觸發 localStorage 變化事件
            window.dispatchEvent(new Event('localStorageChange'));
          }
        }, 100);
      }
    }

    // 處理 404 錯誤：檢查是否為 Tenant 不存在的錯誤
    // 當 tenant 被刪除或不存在時，需要清除本地快取的 tenant_id
    if (response.status === 404) {
      const errorMessage = (errorData.message || '').toLowerCase();
      const errorCode = (errorData.code || '').toUpperCase();

      // 檢查是否為 Tenant 相關的錯誤
      const isTenantNotFound =
        errorCode === 'TENANT_NOT_FOUND' ||
        (errorMessage.includes('tenant') && errorMessage.includes('不存在')) ||
        (errorMessage.includes('tenant') &&
          errorMessage.includes('not found')) ||
        errorMessage.includes('商家不存在') ||
        errorMessage.includes('找不到用戶資訊'); // 這可能是因為 tenant 不存在導致的

      if (isTenantNotFound && typeof window !== 'undefined') {
        const currentPath = window.location.pathname;
        const isAuthPage =
          currentPath === '/' ||
          currentPath.startsWith('/tenant-register') ||
          currentPath.startsWith('/tenant-select') ||
          currentPath.startsWith('/oauth-callback') ||
          currentPath.startsWith('/invite/accept');

        if (!isAuthPage) {
          console.warn(
            '[Fetcher] 檢測到 Tenant 不存在，清除本地快取的 tenant_id'
          );

          // 使用 setTimeout 延遲清除，避免影響正在進行的其他請求
          setTimeout(() => {
            // 再次檢查路徑
            const currentPathAfterDelay = window.location.pathname;
            const isAuthPageAfterDelay =
              currentPathAfterDelay === '/' ||
              currentPathAfterDelay.startsWith('/tenant-register') ||
              currentPathAfterDelay.startsWith('/tenant-select') ||
              currentPathAfterDelay.startsWith('/oauth-callback') ||
              currentPathAfterDelay.startsWith('/invite/accept');

            if (!isAuthPageAfterDelay) {
              // 清除 tenant 相關資訊（但保留 token，因為用戶可能只是切換了 tenant）
              const tenantId = localStorage.getItem('tenant_id');
              localStorage.removeItem('tenant_id');

              // 清除該 tenant 的名稱快取
              if (tenantId) {
                localStorage.removeItem(`tenant_name_${tenantId}`);
              }

              // 觸發 localStorage 變化事件
              window.dispatchEvent(new Event('localStorageChange'));

              // 重定向到 tenant-select 頁面，讓用戶重新選擇 tenant
              // 使用 replace 避免用戶按返回鍵回到錯誤狀態
              window.location.replace('/tenant-select');
            }
          }, 100);
        }
      }
    }

    throw error;
  }

  // 處理空回應（204 No Content 等）
  if (response.status === 204 || response.status === 205) {
    return null as T;
  }

  // 解析 JSON 回應
  try {
    const contentType = response.headers.get('content-type');
    const responseText = await response.text();

    // 除錯：記錄回應內容（對於 feature-flags 請求，總是記錄）
    if (
      import.meta.env.DEV ||
      !response.ok ||
      response.status >= 500 ||
      url.includes('feature-flags')
    ) {
      console.log('[Fetcher] 回應資訊:', {
        url: finalUrl,
        originalUrl: url,
        status: response.status,
        contentType,
        responseLength: responseText.length,
        responsePreview: responseText.substring(0, 500),
        isOk: response.ok,
      });
    }

    if (contentType && contentType.includes('application/json')) {
      if (!responseText || responseText.trim() === '') {
        console.warn('[Fetcher] 回應為空字串，返回空物件');
        return {} as T;
      }
      return JSON.parse(responseText);
    } else {
      // 如果不是 JSON，嘗試解析為 JSON（可能 Content-Type 設定錯誤）
      if (responseText && responseText.trim() !== '') {
        try {
          return JSON.parse(responseText) as T;
        } catch {
          // 如果解析失敗，記錄原始回應
          console.error('[Fetcher] 無法解析回應為 JSON:', {
            contentType,
            responseText: responseText.substring(0, 500),
          });
          throw new ApiError(
            response.status,
            `回應不是有效的 JSON 格式 (Content-Type: ${contentType})`,
            { responseText: responseText.substring(0, 500) }
          );
        }
      }
      return {} as T;
    }
  } catch (parseError) {
    // JSON 解析失敗時，拋出錯誤
    console.error('[Fetcher] JSON 解析錯誤:', {
      url,
      status: response.status,
      error: parseError,
    });
    throw new ApiError(
      response.status,
      'Failed to parse response as JSON',
      parseError
    );
  }
}
